import React, {
  useEffect,
  useState,
  useContext,
  createContext,
  useMemo,
} from 'react';
import Markdown from 'markdown-to-jsx';
import fetchDocumentation from '../../utils/fetchDocumentation';
import Loader from '../../components/Loader';
import File from '../../components/Documentation/File';
import Image from '../../components/Documentation/Image';
import InfoBlock from '../../components/Documentation/InfoBlock';
import Column from '../../components/Documentation/Column';
import AttributesTable from '../../components/Documentation/AttributesTable';
import AppendixTable from '../../components/Documentation/AppendixTable';
import TableRowItem from '../../components/Documentation/TableRowItem';
import PrintTitlePage from '../../components/Documentation/PrintTitlePage';
import AllowedDataSourceList from '../../components/Documentation/AllowedDataSourceList';

import './Documentation.scss';

function AllowedDataSourceListMdx({
  clientConfig,
}) {
  return <AllowedDataSourceList clientConfig={clientConfig} />;
}

function FileMdx({
  name,
  docType,
}) {
  return <File name={name} docType={docType} />;
}

function ImageMdx({
  src,
  alt,
  docType,
  width,
}) {
  return <Image src={src} alt={alt} docType={docType} width={width} />;
}

function FeatureFlagBlockMdx({
  featureFlag,
  featureFlags,
  children,
}) {
  if (!featureFlag) return null;
  if (!featureFlags) return null;
  if (!featureFlags[featureFlag]) return null;
  return children;
}

function DocTable({
  children,
}) {
  return <table className="doc-table">{children}</table>;
}

function DocThead({
  children,
}) {
  return <thead>{children}</thead>;
}

function DocTbody({
  children,
}) {
  return <tbody>{children}</tbody>;
}

function DocTr({
  children,
}) {
  return <tr>{children}</tr>;
}

function DocTh({
  children,
}) {
  return <th>{children}</th>;
}

function DocTd({
  children,
}) {
  return <td>{children}</td>;
}

const DocContext = createContext(null);

function useDocCtx() {
  return useContext(DocContext);
}

function AllowedDataSourceListCtx() {
  const ctx = useDocCtx();
  return <AllowedDataSourceListMdx clientConfig={ctx.clientConfig} />;
}
function FileCtx(props) {
  const ctx = useDocCtx();
  return <FileMdx name={props.name} docType={ctx.docType} />;
}
function ImageCtx(props) {
  const ctx = useDocCtx();
  return <ImageMdx src={props.src} alt={props.alt} docType={ctx.docType} width={props.width} />;
}
function FeatureFlagBlockCtx(props) {
  const ctx = useDocCtx();
  return (
    <FeatureFlagBlockMdx
      featureFlag={props.featureFlag}
      featureFlags={ctx.featureFlags}
    >
      {props.children}
    </FeatureFlagBlockMdx>
  );
}

const formatDocVer = () => {
  const today = new Date();
  const formattedMonth = `0${today.getMonth() + 1}`.slice(-2);
  const formattedDate = `0${today.getDate()}`.slice(-2);
  return `${today.getFullYear()}${formattedMonth}${formattedDate}`;
};

function Documentation(thisProps) {
  const [state, setState] = useState({
    fetching: true,
    docs: '',
  });

  useEffect(() => {
    fetchDocumentation('index.md', thisProps.docType)
      .then((docs) => {
        setState({ docs, fetching: false });
      })
      .catch((error) => {
        setState({ fetching: false });
        console.error('Unable to load docs:', error);
      });
  }, [thisProps.docType]);

  useEffect(() => {
    const { hash } = window.location;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) element.scrollIntoView();
    }
  }, [state.fetching]);

  const contextValue = useMemo(() => ({
    docType: thisProps.docType,
    featureFlags: thisProps.featureFlags,
    clientConfig: thisProps.clientConfig,
  }), [
    thisProps.docType,
    thisProps.featureFlags,
    thisProps.clientConfig,
  ]);

  if (state.fetching) return <Loader />;

  return (
    <div className="documentation-container">
      <PrintTitlePage />
      <DocContext.Provider value={contextValue}>
        <Markdown
          options={{
            overrides: {
              AllowedDataSourceList: { component: AllowedDataSourceListCtx },
              File: { component: FileCtx },
              Image: { component: ImageCtx },
              InfoBlock: { component: InfoBlock },
              Column: { component: Column },
              AttributesTable: { component: AttributesTable },
              AppendixTable: { component: AppendixTable },
              TableRowItem: { component: TableRowItem },
              table: { component: DocTable },
              thead: { component: DocThead },
              tbody: { component: DocTbody },
              tr: { component: DocTr },
              th: { component: DocTh },
              td: { component: DocTd },
              FeatureFlagBlock: { component: FeatureFlagBlockCtx },
            },
          }}
        >
          { state.docs }
        </Markdown>
      </DocContext.Provider>
      <div className="print-only-doc-footer">
        Trust Science
        <sup>®</sup>
        &nbsp;
        Six°Score™ API Documentation [DocVer:
        {formatDocVer()}
        ]
      </div>
    </div>
  );
}

export default Documentation;
